import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { updateNavbarHeading } from "../../../constants/layout/store/filterSlice";
import Select from 'react-select';
import CustomDatePicker from "../../../components/Custom-Datepicker";
import EditorComponent from "../../../components/editor";
import {
  GetServiceCaller,
  PostServiceCaller,
  PostFormServiceCaller,
} from "../../../services/serviceCaller";
import { API_URL } from "../../../constants";

const AddRequisitionResource = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(updateNavbarHeading("Add Requisition Resource"));
    selectListData();
  }, [dispatch]);

  /** =========================
   * STATES
   * ========================= */
  const [formData, setFormData] = useState({
    JobCode: "",
    PositionName: "",
    Description: "",
    NumOpenings: "",
    OpeningType: "",
    RequirementType: "",
    RequisitionStatus: "Open",
    RequirementHiringType: "",
    LocationType: "",
    Location: "",
    MinExp: "",
    MaxExp: "",
    RomID: "",
    JobStatus: "Open", // Changed default value from "Active" to "Open"
    Reason: "",
    JobStatsChangeDate: "",
    Priority: "",
    Role: "",
    RequirementDate: "",
    skill: [], 
    SubSkills: [], 
    ProjectType: "Consulting",
    AccountManager: "",
    RegionID: "",
  });

  const [errors, setErrors] = useState({});
  const [pageLoading, setPageLoading] = useState(false);

  /** =========================
   * DROPDOWN OPTIONS STATES
   * ========================= */
  const [jobTitleOptions, setJobTitleOptions] = useState([]);
  const [skillSetOptions, setSkillSetOptions] = useState([]);
  const [subSkillOptions, setSubSkillOptions] = useState([]);
  const [locationTypeOptions, setLocationTypeOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [requirementTypeOptions, setRequirementTypeOptions] = useState([]);
  const [jobStatusOptions, setjobStatusOptions] = useState([]);
  
  const [locationOptions, setLocationOptions] = useState([]);
  const [romPocOptions, setRomPocOptions] = useState([]);
  const [accManagerOptions, setAccManagerOptions] = useState([]);

  // Static options
  const openingTypeOptions = [
    { value: "New Resource", label: "New Resource" },
    { value: "Replacement", label: "Replacement" }
  ];

  const projectTypeOption = [
    { value: "Consulting", label: "Consulting" },
    { value: "Content", label: "Content" },
    { value: "Training", label: "Training" },
  ];

  const priorityOptions = [
    { value: "", label: "Select" },
    { value: "3", label: "Low" },
    { value: "2", label: "Med" },
    { value: "1", label: "High" },
  ];

  /** =========================
   * API CALLS
   * ========================= */
  const selectListData = async () => {
    setPageLoading(true);
    try {
      const response = await GetServiceCaller(`${API_URL}get-Requisition-details`);

      if (response.status === 200) {
        const data = response?.data.data;

        /* JOB TITLE */
        const jobTitleData = data?.jobtitle?.map((record) => ({
          value: record.value,
          label: record.text,
        }));
        setJobTitleOptions(jobTitleData);

        /* SKILLS */
        const skillData = data?.Skill?.map((record) => ({
          value: record.ID,
          label: record.Name,
        }));
        setSkillSetOptions(skillData);

        /* LOCATION TYPE */
        if (data?.locationType) {
          const locationTypeData = Object.entries(data.locationType).map(([key, val]) => ({
            value: key,
            label: val
          }));
          setLocationTypeOptions(locationTypeData);
        }

        /* REGION */
        const regionData = data?.region?.map((record) => ({
          value: record.value,
          label: record.text,
        }));
        setRegionOptions(regionData);

        /* REQUIREMENT TYPE */
        const requirementTypeData = data?.requirementType?.map((record) => ({
          value: record.value,
          label: record.text,
        }));
        setRequirementTypeOptions(requirementTypeData);

        /* Job STATUS */
        const jobStatusOptions = data?.jobstatus?.map((record) => ({
          value: record.value,
          label: record.text,
        }));
        setjobStatusOptions(jobStatusOptions);

        /* LOCATION */
        const parsedLocationData = data?.location?.map((record) => ({
          value: record.value || record.ID, 
          label: record.text || record.Name,
        })) || [];
        setLocationOptions(parsedLocationData);

        /* ROM ID */
        const parsedRomData = data?.rompoc?.map((record) => ({
          value: record.value || record.ID,
          label: record.text || record.Name,
        })) || [];
        setRomPocOptions(parsedRomData);

        /* ACCOUNT MANAGER */
        const parsedAccManagerData = data?.accmanager?.map((record) => ({
          value: record.value || record.ID,
          label: record.text || record.Name,
        })) || [];
        setAccManagerOptions(parsedAccManagerData);
        
        setPageLoading(false);
      } else {
        setPageLoading(false);
      }
    } catch (error) {
      setPageLoading(false);
      console.error("Error fetching dropdown data:", error);
    }
  };

  // Target dynamic conditional dependency parsing array sets
  const fetchSubSkillOptions = async (skill_id) => {
    try {
      let data = { skillId: skill_id };
      const response = await PostServiceCaller(
        API_URL + "fetch-subskils",
        data,
        "post"
      );
      const subSkillData = response.data;

      const options = subSkillData.map((subSkill) => ({
        value: subSkill.ID,
        label: subSkill.SubSkill,
      }));
      setSubSkillOptions(options);
    } catch (error) {
      console.error("Error fetching subSkill options:", error);
    }
  };

  /** =========================
   * CUSTOM STYLE
   * ========================= */
  const customStyle = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "30px",
      height: "30px",
      fontSize: "12px",
      borderColor: state.isFocused ? "#86b7fe" : "#dee2e6",
      boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)" : "none",
      "&:hover": { borderColor: "#86b7fe" },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0px 6px",
      height: "30px",
      display: "flex",
      alignItems: "center",
      flexWrap: "nowrap",
      overflow: "hidden",
    }),
    input: (provided) => ({ ...provided, margin: "0px", padding: "0px" }),
    indicatorsContainer: (provided) => ({ ...provided, height: "30px" }),
    indicatorContainer: (provided) => ({ ...provided, padding: "2px" }),
    dropdownIndicator: (provided) => ({ ...provided, padding: "2px 4px" }),
    clearIndicator: (provided) => ({ ...provided, padding: "2px 4px" }),
    multiValue: (provided) => ({ ...provided, backgroundColor: "#e9ecef", margin: "1px", borderRadius: "4px" }),
    multiValueLabel: (provided) => ({ ...provided, fontSize: "11px", padding: "0px 4px", color: "#495057" }),
    multiValueRemove: (provided) => ({ ...provided, "&:hover": { backgroundColor: "#dc3545", color: "white" } }),
  };

  /** =========================
   * HANDLERS & VALIDATION
   * ========================= */
  const validateField = (fieldName, value) => {
    let newErrors = { ...errors };
    if (!value || (typeof value === "string" && value.trim() === "")) {
      newErrors[fieldName] = "This field is required";
    } else {
      delete newErrors[fieldName];
    }
    setErrors(newErrors);
    return !newErrors[fieldName];
  };

  const handleChange = (e) => {
    const name = e?.target ? e.target.name : e?.name;
    const value = e?.target ? e.target.value : e?.value;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelect = (selectedOption, fieldName) => {
    const val = selectedOption ? selectedOption.value : "";
    setFormData((prev) => ({ ...prev, [fieldName]: val }));
  };

 const handleMultiSelect = (selectedOptions, fieldName) => {
    const values = selectedOptions ? selectedOptions.map((option) => option.value) : [];
    setFormData((prev) => ({ ...prev, [fieldName]: values }));

    // Whenever skills change, grab the newest/last selected skill ID and pass it to your API
    if (fieldName === "skill" && values.length > 0) {
      const latestSkillId = values[values.length - 1]; 
      fetchSubSkillOptions(latestSkillId);
    } else if (fieldName === "skill" && values.length === 0) {
      // Clear sub-skill options if all primary skills are removed
      setSubSkillOptions([]);
    }
 };

  /** =========================
   * SUBMIT WITH VALUE & LABEL PAYLOAD
   * ========================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const isDescriptionValid = validateField("Description", formData.Description);
    if (!isDescriptionValid) {
      console.warn("Form Submission Blocked: Required validation failed.");
      return;
    }

    setPageLoading(true);
    try {
      const payload = {
        ...formData,
        skill: formData.skill.map(id => {
          const match = skillSetOptions.find(opt => opt.value === id);
          return {
            value: id,
            label: match ? match.label : ""
          };
        }),
        SubSkills: formData.SubSkills.map(id => {
          const match = subSkillOptions.find(opt => opt.value === id);
          return {
            value: id,
            label: match ? match.label : ""
          };
        })
      };

      const response = await PostServiceCaller(
        `${API_URL}requisition-resource`,
        payload, 
        "post"
      );

      if (response && (response.status === 200 || response.status === 201)) {
        console.log("Form saved successfully:", response.data);
        alert("Requisition saved successfully!");
      } else {
        console.error("Submission failed with unmapped status code response:", response);
        alert("Failed to save transaction data.");
      }
    } catch (error) {
      console.error("Error executing form post transaction actions:", error);
      alert("An unexpected error occurred during submission.");
    } finally {
      setPageLoading(false);
    }
  };

  return (
    <div className="bg-light min-vh-100 py-4">
      <div className="container-fluid px-4">
        <div className="card shadow-sm border-0 rounded-3 mx-auto" style={{ maxWidth: "1650px" }}>
          <form onSubmit={handleSubmit} className="card-body p-4 bg-white">
            
            {/* Main Configuration Grid */}
            <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5 g-3 mb-4">
              
              {/* JOB CODE */}
              <div className="col">
                <label className="form-label text-dark fw-bold mb-1" style={{ fontSize: "11px" }}>JOB CODE</label>
                <input type="text" name="JobCode" value={formData.JobCode} disabled placeholder="Auto Generated" className="form-control form-control-sm bg-light text-muted" style={{ height: "30px", fontSize: "12px" }} />
              </div>

              {/* REQ DATE */}
              <div className="col">
                <label className="form-label text-dark fw-bold mb-1" style={{ fontSize: "11px" }}>REQ DATE</label>
                <div style={{ height: "30px" }} className="datepicker-container-sm">
                  <CustomDatePicker handleChange={(e) => handleChange(e)} value={formData.RequirementDate} name="RequirementDate" className="form-control form-control-sm" placeholderText="DD-MMM-YYYY" />
                </div>
              </div>

              {/* POSITION / JOB TITLE */}
              <div className="col">
                <label className="form-label text-dark fw-bold mb-1" style={{ fontSize: "11px" }}>POSITION / JOB TITLE</label>
                <Select name="PositionName" value={jobTitleOptions.find((opt) => opt.value === formData.PositionName) || null} onChange={(selectedOption, action) => handleSelect(selectedOption, action.name)} options={jobTitleOptions} styles={customStyle} placeholder="Select Title..." classNamePrefix="select" isClearable />
              </div>

              {/* ROLE */}
              <div className="col">
                <label className="form-label text-dark fw-bold mb-1" style={{ fontSize: "11px" }}>ROLE</label>
                <input type="text" name="Role" value={formData.Role} onChange={handleChange} placeholder="e.g. Associate" className="form-control form-control-sm" style={{ height: "30px", fontSize: "12px" }} />
              </div>

              {/* OPENING TYPE */}
              <div className="col">
                <label className="form-label text-dark fw-bold mb-1" style={{ fontSize: "11px" }}>OPENING TYPE</label>
                <Select name="OpeningType" value={openingTypeOptions.find((opt) => opt.value === formData.OpeningType) || null} onChange={(selectedOption, action) => handleSelect(selectedOption, action.name)} options={openingTypeOptions} styles={customStyle} placeholder="Select..." classNamePrefix="select" isClearable />
              </div>

              {/* EXPERIENCE (MIN / MAX) */}
              <div className="col">
                <label className="form-label text-dark fw-bold mb-1" style={{ fontSize: "11px" }}>EXPERIENCE (MIN / MAX)</label>
                <div className="input-group input-group-sm" style={{ height: "30px" }}>
                  <input type="number" name="MinExp" value={formData.MinExp} onChange={handleChange} placeholder="Min" className="form-control text-center" style={{ fontSize: "12px", height: "30px" }} />
                  <span className="input-group-text bg-light border-start-0 border-end-0 text-muted px-2" style={{ fontSize: "11px", height: "30px" }}>To</span>
                  <input type="number" name="MaxExp" value={formData.MaxExp} onChange={handleChange} placeholder="Max" className="form-control text-center" style={{ fontSize: "12px", height: "30px" }} />
                </div>
              </div>

              {/* REQUIREMENT TYPE */}
              <div className="col">
                <label className="form-label text-dark fw-bold mb-1" style={{ fontSize: "11px" }}>REQUIREMENT TYPE</label>
                <Select name="RequirementType" value={requirementTypeOptions.find((opt) => opt.value === formData.RequirementType) || null} onChange={(selectedOption, action) => handleSelect(selectedOption, action.name)} options={requirementTypeOptions} styles={customStyle} placeholder="Select..." classNamePrefix="select" isClearable />
              </div>

              {/* LOCATION TYPE */}
              <div className="col">
                <label className="form-label text-dark fw-bold mb-1" style={{ fontSize: "11px" }}>LOCATION TYPE</label>
                <Select name="LocationType" value={locationTypeOptions.find((opt) => opt.value === formData.LocationType) || null} onChange={(selectedOption, action) => handleSelect(selectedOption, action.name)} options={locationTypeOptions} styles={customStyle} placeholder="Select..." classNamePrefix="select" isClearable />
              </div>

              {/* LOCATION */}
              <div className="col">
                <label className="form-label text-dark fw-bold mb-1" style={{ fontSize: "11px" }}>LOCATION</label>
                <Select name="Location" value={locationOptions.find((opt) => opt.value === formData.Location) || null} onChange={(selectedOption, action) => handleSelect(selectedOption, action.name)} options={locationOptions} styles={customStyle} placeholder="Select Location..." classNamePrefix="select" isClearable />
              </div>

              {/* PRIORITY */}
              <div className="col">
                <label className="form-label text-dark fw-bold mb-1" style={{ fontSize: "11px" }}>PRIORITY</label>
                <Select name="Priority" value={priorityOptions.find((opt) => opt.value === formData.Priority) || null} onChange={(selectedOption, action) => handleSelect(selectedOption, action.name)} options={priorityOptions} styles={customStyle} placeholder="Select..." classNamePrefix="select" isClearable />
              </div>

              {/* PROJECT TYPE */}
              <div className="col">
                <label className="form-label text-dark fw-bold mb-1" style={{ fontSize: "11px" }}>PROJECT TYPE</label>
                <Select name="ProjectType" value={projectTypeOption.find((opt) => opt.value === formData.ProjectType) || null} onChange={(selectedOption, action) => handleSelect(selectedOption, action.name)} options={projectTypeOption} styles={customStyle} placeholder="Select..." classNamePrefix="select" isClearable />
              </div>

              {/* ROM ID */}
              <div className="col">
                <label className="form-label text-dark fw-bold mb-1" style={{ fontSize: "11px" }}>ROM ID</label>
                <Select name="RomID" value={romPocOptions.find((opt) => opt.value === formData.RomID) || null} onChange={(selectedOption, action) => handleSelect(selectedOption, action.name)} options={romPocOptions} styles={customStyle} placeholder="Select ROM ID..." classNamePrefix="select" isClearable />
              </div>

              {/* REGION ID */}
              <div className="col">
                <label className="form-label text-dark fw-bold mb-1" style={{ fontSize: "11px" }}>REGION ID</label>
                <Select name="RegionID" value={regionOptions.find((opt) => opt.value === formData.RegionID) || null} onChange={(selectedOption, action) => handleSelect(selectedOption, action.name)} options={regionOptions} styles={customStyle} placeholder="Select..." classNamePrefix="select" isClearable />
              </div>

              {/* ACCOUNT MANAGER */}
              <div className="col">
                <label className="form-label text-dark fw-bold mb-1" style={{ fontSize: "11px" }}>ACCOUNT MANAGER</label>
                <Select name="AccountManager" value={accManagerOptions.find((opt) => opt.value === formData.AccountManager) || null} onChange={(selectedOption, action) => handleSelect(selectedOption, action.name)} options={accManagerOptions} styles={customStyle} placeholder="Select Manager..." classNamePrefix="select" isClearable />
              </div>

              {/* JOB STATUS */}
              <div className="col">
                <label className="form-label text-dark fw-bold mb-1" style={{ fontSize: "11px" }}>JOB STATUS</label>
                <Select name="JobStatus" value={jobStatusOptions.find((opt) => opt.value === formData.JobStatus) || null} onChange={(selectedOption, action) => handleSelect(selectedOption, action.name)} options={jobStatusOptions} styles={customStyle} placeholder="Select..." classNamePrefix="select" isClearable />
              </div>

            </div>

            {/* Inline Control Segment Row */}
            <div className="row g-3 align-items-start mb-4">

              {/* OPENINGS */}
              <div className="col-12 col-sm-6 col-md-2 col-xl-1">
                <label className="form-label text-dark fw-bold mb-1 text-nowrap" style={{ fontSize: "11px" }}>OPENINGS</label>
                <input type="number" name="NumOpenings" value={formData.NumOpenings} onChange={handleChange} min="1" placeholder="0" className="form-control form-control-sm" style={{ height: "30px", fontSize: "12px" }} />
              </div>

              {/* PRIMARY SKILL */}
              <div className="col-12 col-sm-6 col-md-3 col-xl-2">
                <label className="form-label text-dark fw-bold mb-1" style={{ fontSize: "11px" }}>PRIMARY SKILL</label>
                <Select menuShouldScrollIntoView isMulti styles={customStyle} name="skill" classNamePrefix="select" options={skillSetOptions} placeholder="Select..." value={skillSetOptions.filter((option) => formData.skill.includes(option.value))} onChange={(selectedOptions) => handleMultiSelect(selectedOptions, "skill")} />
              </div>

              {/* SUB SKILLS */}
              <div className="col-12 col-sm-6 col-md-4 col-xl-2">
                <label className="form-label text-dark fw-bold mb-1" style={{ fontSize: "11px" }}>SUB SKILLS</label>
                <Select menuShouldScrollIntoView isMulti styles={customStyle} name="SubSkills" classNamePrefix="select" options={subSkillOptions} placeholder="Select..." value={subSkillOptions.filter((option) => formData.SubSkills.includes(option.value))} onChange={(selectedOptions) => handleMultiSelect(selectedOptions, "SubSkills")} />
              </div>

              {/* DESCRIPTION */}
              <div className="col-12 col-md-12 col-xl-5">
                <label className="form-label text-dark fw-bold mb-1" style={{ fontSize: "11px" }}>DESCRIPTION</label>
                <span className="text-danger">*</span>
                <EditorComponent
                  value={formData.Description || ""}
                  className={` ${errors?.Description ? "is-invalid" : ""}`}
                  onEditorChange={(content) => {
                    setFormData({ ...formData, Description: content });
                    validateField("Description", content);
                  }}
                />
                {errors?.Description && <span className="error text-danger d-block mt-1" style={{ fontSize: "11px" }}>{errors?.Description}</span>}
              </div>

            </div>

            {/* ACTION BUTTON GROUPS */}
            <div className="d-flex justify-content-end gap-2 pt-3 border-top">
              <button type="button" className="btn btn-sm btn-outline-secondary px-3 py-1" style={{ fontSize: "12px" }}>Cancel</button>
              <button type="submit" disabled={pageLoading} className="btn btn-sm btn-primary px-4 py-1 shadow-sm" style={{ fontSize: "12px" }}>
                {pageLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};
export default AddRequisitionResource;